'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function DiscountCalculator() {
  const t = useTranslations("tools.discountCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    originalPrice: '',
    discountValue: '',
    discountType: 'percent' as 'percent' | 'fixed'
  });

  const [results, setResults] = useState<{
    finalPrice: number;
    savingsAmount: number;
    discountPercent: number;
    originalPrice: number;
  } | null>(null);

  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const originalPrice = parseFloat(inputs.originalPrice);
    const discountValue = parseFloat(inputs.discountValue);
    const isPercent = inputs.discountType === 'percent';

    if (isNaN(originalPrice) || originalPrice <= 0) {
      setError(t('errorInvalidPrice'));
      setResults(null);
      return;
    }

    if (isNaN(discountValue) || discountValue < 0) {
      setError(t('errorInvalidDiscount'));
      setResults(null);
      return;
    }

    let savingsAmount: number;
    let discountPercent: number;

    if (isPercent) {
      if (discountValue > 100) {
        setError(t('errorPercentExceeds'));
        setResults(null);
        return;
      }
      discountPercent = discountValue;
      savingsAmount = originalPrice * (discountValue / 100);
    } else {
      if (discountValue > originalPrice) {
        setError(t('errorAmountExceeds'));
        setResults(null);
        return;
      }
      savingsAmount = discountValue;
      discountPercent = (discountValue / originalPrice) * 100;
    }

    const finalPrice = originalPrice - savingsAmount;

    setResults({
      finalPrice,
      savingsAmount,
      discountPercent,
      originalPrice
    });
  };

  const formatCurrency = (value: number) => {
    return '$' + value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
    success: '#10b981',
    error: '#dc2626',
    errorBg: '#fef2f2',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden' as const,
      margin: '0 auto'
    },
    { maxWidth: '100%' },
    { maxWidth: '600px' },
    { maxWidth: '800px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      textAlign: 'center' as const
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const toolHeaderTitleStyle = getResponsiveStyle(
    { margin: 0, fontWeight: 700 },
    { fontSize: '1.5rem' },
    { fontSize: '1.625rem' },
    { fontSize: '1.75rem' }
  );

  const formStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const formGroupStyle = getResponsiveStyle(
    {},
    { marginBottom: '1.25rem' },
    { marginBottom: '1.375rem' },
    { marginBottom: '1.5rem' }
  );

  const labelStyle = {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: 600,
    color: '#1e293b',
    fontSize: '0.95rem'
  };

  const inputWrapperStyle = {
    position: 'relative' as const
  };

  const inputStyle = {
    width: '100%',
    padding: '0.875rem 1rem',
    border: '2px solid #e2e8f0',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    transition: 'all 0.3s ease',
    boxSizing: 'border-box' as const,
    outline: 'none'
  };

  const inputPrefixStyle = {
    ...inputStyle,
    paddingLeft: '2rem'
  };

  const inputSuffixStyle = {
    ...inputStyle,
    paddingRight: '2.5rem'
  };

  const prefixSymbolStyle = {
    position: 'absolute' as const,
    left: '1rem',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#64748b',
    fontWeight: 600
  };

  const suffixSymbolStyle = {
    position: 'absolute' as const,
    right: '1rem',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#64748b',
    fontWeight: 600
  };

  const discountTypeStyle = getResponsiveStyle(
    { display: 'flex', marginTop: '1rem' },
    { gap: '1rem', flexDirection: 'column' as const },
    { gap: '1.25rem', flexDirection: 'row' as const },
    { gap: '1.5rem', flexDirection: 'row' as const }
  );

  const radioOptionStyle = {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer'
  };

  const radioInputStyle = {
    width: 'auto',
    marginRight: '0.5rem',
    cursor: 'pointer'
  };

  const radioLabelStyle = {
    margin: 0,
    cursor: 'pointer',
    fontWeight: 500
  };

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '1rem'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem' },
    { padding: '1.125rem 2.5rem', fontSize: '1.125rem' }
  );

  const errorMessageStyle = {
    background: colors.errorBg,
    color: colors.error,
    padding: '1rem',
    borderRadius: '0.5rem',
    marginTop: '1rem',
    borderLeft: `4px solid ${colors.error}`,
    display: error ? 'block' : 'none'
  };

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      borderTop: '3px solid #e2e8f0',
      display: results ? 'block' : 'none'
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultsHeaderStyle = getResponsiveStyle(
    { margin: '0 0 1.5rem 0', color: '#1e293b', textAlign: 'center' as const },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const resultGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem', marginBottom: '1.5rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(3, 1fr)' }
  );

  const resultItemStyle = {
    background: 'white',
    padding: '1.25rem',
    borderRadius: '0.75rem',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    borderLeft: '4px solid #2563eb'
  };

  const resultLabelStyle = {
    display: 'block',
    fontSize: '0.875rem',
    color: '#64748b',
    marginBottom: '0.5rem',
    fontWeight: 500
  };

  const resultValueStyle = getResponsiveStyle(
    { display: 'block', fontWeight: 700, color: '#1e293b' },
    { fontSize: '1.5rem' },
    { fontSize: '1.625rem' },
    { fontSize: '1.75rem' }
  );

  const resultValueHighlightStyle = {
    ...resultValueStyle,
    color: '#2563eb'
  };

  const resultValueSavingsStyle = {
    ...resultValueStyle,
    color: colors.success
  };

  const breakdownStyle = {
    background: 'white',
    padding: '1.5rem',
    borderRadius: '0.75rem',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
  };

  const breakdownHeaderStyle = {
    margin: '0 0 1rem 0',
    color: '#1e293b',
    fontSize: '1.1rem'
  };

  const breakdownItemStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.75rem 0',
    borderBottom: '1px solid #e2e8f0'
  };

  const breakdownItemFinalStyle = {
    ...breakdownItemStyle,
    borderBottom: 'none',
    fontWeight: 700,
    color: '#2563eb',
    fontSize: '1.1rem'
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)'
    },
    { padding: '1.5rem', marginBottom: '1.5rem' },
    { padding: '2rem', marginBottom: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', marginBottom: '2.5rem', borderRadius: '1.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem', marginTop: 0 },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem', marginBottom: '1.25rem' },
    { fontSize: '2rem', marginBottom: '1.5rem' }
  );

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.75rem', marginTop: '1.5rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const listStyle = {
    ...paragraphStyle,
    paddingLeft: '1.5rem'
  };

  const listItemStyle = {
    marginBottom: '0.75rem'
  };

  const strongStyle = {
    fontWeight: 600,
    color: colors.textPrimary
  };

  const linkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    borderBottom: `1px solid ${colors.primary}`,
    transition: 'all 0.3s ease'
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderTitleStyle}>{t('toolTitle')}</h2>
          </div>

          <form style={formStyle} onSubmit={handleCalculate}>
            <div style={formGroupStyle}>
              <label style={labelStyle} htmlFor="original-price">{t('originalPriceLabel')}</label>
              <div style={inputWrapperStyle}>
                <span style={prefixSymbolStyle}>$</span>
                <input
                  type="number"
                  id="original-price"
                  step="0.01"
                  min="0"
                  placeholder={t('originalPricePlaceholder')}
                  value={inputs.originalPrice}
                  onChange={(e) => setInputs({...inputs, originalPrice: e.target.value})}
                  style={inputPrefixStyle}
                  required
                />
              </div>
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle} htmlFor="discount-value">{t('discountLabel')}</label>
              <div style={inputWrapperStyle}>
                {inputs.discountType === 'percent' ? (
                  <>
                    <span style={suffixSymbolStyle}>%</span>
                    <input
                      type="number"
                      id="discount-value"
                      step="0.01"
                      min="0"
                      placeholder={t('discountPercentPlaceholder')}
                      value={inputs.discountValue}
                      onChange={(e) => setInputs({...inputs, discountValue: e.target.value})}
                      style={inputSuffixStyle}
                      required
                    />
                  </>
                ) : (
                  <>
                    <span style={prefixSymbolStyle}>$</span>
                    <input
                      type="number"
                      id="discount-value"
                      step="0.01"
                      min="0"
                      placeholder={t('discountAmountPlaceholder')}
                      value={inputs.discountValue}
                      onChange={(e) => setInputs({...inputs, discountValue: e.target.value})}
                      style={inputPrefixStyle}
                      required
                    />
                  </>
                )}
              </div>
              <div style={discountTypeStyle}>
                <div style={radioOptionStyle}>
                  <input
                    type="radio"
                    id="percent-off"
                    name="discount-type"
                    value="percent"
                    checked={inputs.discountType === 'percent'}
                    onChange={() => setInputs({...inputs, discountType: 'percent'})}
                    style={radioInputStyle}
                  />
                  <label style={radioLabelStyle} htmlFor="percent-off">{t('percentOff')}</label>
                </div>
                <div style={radioOptionStyle}>
                  <input
                    type="radio"
                    id="fixed-off"
                    name="discount-type"
                    value="fixed"
                    checked={inputs.discountType === 'fixed'}
                    onChange={() => setInputs({...inputs, discountType: 'fixed'})}
                    style={radioInputStyle}
                  />
                  <label style={radioLabelStyle} htmlFor="fixed-off">{t('fixedAmountOff')}</label>
                </div>
              </div>
            </div>

            <div style={errorMessageStyle}>{error}</div>

            <button type="submit" style={calculateButtonStyle}>{t('calculateButton')}</button>
          </form>

          <div style={resultsStyle}>
            <h3 style={resultsHeaderStyle}>{t('resultsTitle')}</h3>

            <div style={resultGridStyle}>
              <div style={resultItemStyle}>
                <span style={resultLabelStyle}>{t('finalPriceLabel')}</span>
                <span style={resultValueHighlightStyle}>
                  {results ? formatCurrency(results.finalPrice) : '$0.00'}
                </span>
              </div>

              <div style={resultItemStyle}>
                <span style={resultLabelStyle}>{t('savingsLabel')}</span>
                <span style={resultValueSavingsStyle}>
                  {results ? formatCurrency(results.savingsAmount) : '$0.00'}
                </span>
              </div>

              <div style={resultItemStyle}>
                <span style={resultLabelStyle}>{t('discountPercentLabel')}</span>
                <span style={resultValueStyle}>
                  {results ? `${results.discountPercent.toFixed(2)}%` : '0%'}
                </span>
              </div>
            </div>

            <div style={breakdownStyle}>
              <h4 style={breakdownHeaderStyle}>{t('breakdownTitle')}</h4>
              <div style={breakdownItemStyle}>
                <span>{t('breakdownOriginal')}</span>
                <span>{results ? formatCurrency(results.originalPrice) : '$0.00'}</span>
              </div>
              <div style={breakdownItemStyle}>
                <span>
                  {results
                    ? `${t('breakdownDiscount')} (${results.discountPercent.toFixed(2)}%)`
                    : `${t('breakdownDiscount')} (0%)`
                  }
                </span>
                <span>{results ? `-${formatCurrency(results.savingsAmount)}` : '-$0.00'}</span>
              </div>
              <div style={breakdownItemFinalStyle}>
                <span>{t('breakdownFinal')}</span>
                <span>{results ? formatCurrency(results.finalPrice) : '$0.00'}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step1Title')}</strong> {t('step1Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step2Title')}</strong> {t('step2Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step3Title')}</strong> {t('step3Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step4Title')}</strong> {t('step4Description')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insightsParagraph1')}</p>

        <h3 style={h3Style}>{t('insightsLatestTitle')}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('insightsLatest1')}</li>
          <li style={listItemStyle}>{t('insightsLatest2')}</li>
          <li style={listItemStyle}>{t('insightsLatest3')}</li>
        </ul>

        <h3 style={h3Style}>{t('insightsBestPracticesTitle')}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('insightsBestPractice1')}</li>
          <li style={listItemStyle}>{t('insightsBestPractice2')}</li>
          <li style={listItemStyle}>{t('insightsBestPractice3')}</li>
          <li style={listItemStyle}>{t('insightsBestPractice4')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>

        <h3 style={h3Style}>{t('detailsConsumersTitle')}</h3>
        <p style={paragraphStyle}>{t('detailsConsumers')}</p>

        <h3 style={h3Style}>{t('detailsRetailersTitle')}</h3>
        <p style={paragraphStyle}>{t('detailsRetailers')}</p>

        <h3 style={h3Style}>{t('detailsBusinessesTitle')}</h3>
        <p style={paragraphStyle}>{t('detailsBusinesses')}</p>

        <h3 style={h3Style}>{t('detailsTypesTitle')}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t('detailsType1Title')}</strong> {t('detailsType1Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('detailsType2Title')}</strong> {t('detailsType2Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('detailsType3Title')}</strong> {t('detailsType3Description')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <h3 style={h3Style}>{t('faq1Question')}</h3>
        <p style={paragraphStyle}>{t('faq1Answer')}</p>

        <h3 style={h3Style}>{t('faq2Question')}</h3>
        <p style={paragraphStyle}>{t('faq2Answer')}</p>

        <h3 style={h3Style}>{t('faq3Question')}</h3>
        <p style={paragraphStyle}>{t('faq3Answer')}</p>

        <h3 style={h3Style}>{t('faq4Question')}</h3>
        <p style={paragraphStyle}>{t('faq4Answer')}</p>

        <h3 style={h3Style}>{t('faq5Question')}</h3>
        <p style={paragraphStyle}>{t('faq5Answer')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>
            <a href="https://brightseotools.com/discount-calculator" style={linkStyle} target="_blank" rel="noopener noreferrer">
              {t('reference1')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://bikayi.com/tools/discount-calculator" style={linkStyle} target="_blank" rel="noopener noreferrer">
              {t('reference2')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://activecalculator.com/templates/discount-calculator" style={linkStyle} target="_blank" rel="noopener noreferrer">
              {t('reference3')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.webretailer.com/discount-calculator/" style={linkStyle} target="_blank" rel="noopener noreferrer">
              {t('reference4')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://mercadokit.com/tools/bundle-calculator" style={linkStyle} target="_blank" rel="noopener noreferrer">
              {t('reference5')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.gigacalculator.com/calculators/discount-calculator.php" style={linkStyle} target="_blank" rel="noopener noreferrer">
              {t('reference6')}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}